import { useState, useEffect } from 'react';
import { initDB, getAllFromStore, saveToStore, deleteFromStore, addToSyncQueue, STORES } from '../utils/offlineStorage';
import SyncManager from '../utils/syncManager';

export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [manualMode, setManualMode] = useState(false); // Manual override
  const [manualIsOnline, setManualIsOnline] = useState(true);
  const [syncStatus, setSyncStatus] = useState({ syncing: false, pendingCount: 0 });
  const [syncManager] = useState(() => new SyncManager((status) => setSyncStatus(status)));

  // Effective online status (manual override or automatic)
  const effectiveOnline = manualMode ? manualIsOnline : isOnline;

  useEffect(() => {
    // Initialize IndexedDB
    initDB().catch(err => console.error('Failed to init DB:', err));

    // Online/offline event listeners (only used when not in manual mode)
    const handleOnline = () => {
      console.log('Network is online');
      if (!manualMode) {
        setIsOnline(true);
        // Automatically sync when coming back online
        syncManager.syncAll().catch(err => console.error('Auto-sync failed:', err));
      }
    };

    const handleOffline = () => {
      console.log('Network is offline');
      if (!manualMode) {
        setIsOnline(false);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check for pending sync items on mount
    syncManager.getPendingCount().then(count => {
      if (count > 0) {
        setSyncStatus(prev => ({ ...prev, pendingCount: count }));
      }
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncManager, manualMode]);

  const manualSync = async () => {
    try {
      const result = await syncManager.syncAll();
      return result;
    } catch (error) {
      console.error('Manual sync failed:', error);
      throw error;
    }
  };

  const toggleManualMode = (forceOffline) => {
    setManualMode(true);
    setManualIsOnline(!forceOffline);
    if (!forceOffline) {
      // Switching to online manually, trigger sync
      syncManager.syncAll().catch(err => console.error('Manual sync failed:', err));
    }
  };

  const disableManualMode = () => {
    setManualMode(false);
    setIsOnline(navigator.onLine);
    if (navigator.onLine) {
      // Sync when switching back to auto mode if online
      syncManager.syncAll().catch(err => console.error('Auto-sync failed:', err));
    }
  };

  return {
    isOnline: effectiveOnline,
    syncStatus,
    manualSync,
    syncManager,
    manualMode,
    toggleManualMode,
    disableManualMode
  };
};

export default useOffline;
